<template>
  <v-dialog v-model="dialog" persistent width="800px">
    <v-card>
      <v-card-title class="mx-3">
        <span class="accent--text pe-1">
          {{ value ? value.material_name : "" }}
        </span>
        <span v-if="percent && settings && settings.sub_account_split_type">
          {{ $t("MaterialTemplate.splitPercent") }}
        </span>
        <span v-else>
          {{ $t("MaterialTemplate.splitPrice") }}
        </span>

        <v-spacer></v-spacer>
        <span v-if="percent && settings && settings.sub_account_split_type">
        </span>
        <span v-else>
          {{ $t("MaterialTemplate.totalPrice") }}{{ $t("Global.colon") }}
          <span class="accent--text pe-4">
            {{ $t("Global.money") }}
            {{ price | formatMoney }}
          </span>
        </span>
        <v-tooltip left>
          <template v-slot:activator="{ on, attrs }">
            <v-icon v-bind="attrs" v-on="on" @click="dialog = false">
              fa-times
            </v-icon>
          </template>
          <span>{{ $t("$vuetify.close") }}</span>
        </v-tooltip>
      </v-card-title>

      <ValidationObserver ref="observer">
        <v-data-table
          :headers="headers"
          :items="sub_account_prices"
          :loading="loading"
          multi-sort
          disable-pagination
          hide-default-footer
          class="elevation-0 px-6"
          height="400px"
          fixed-header
        >
          <template v-slot:header.price="{ header }">
            <span v-if="percent && settings && settings.sub_account_split_type">
              {{ $t("MaterialTemplate.splitPrices.percent") }}
            </span>
            <span v-else>
              {{ header.text }}
            </span>
          </template>

          <template v-slot:item.account="{ item }">
            <ValidationProvider
              v-slot="{ errors }"
              :name="item.type"
              :rules="Number(item.require) == 1 ? 'required' : ''"
            >
              <v-select
                v-model="item.sub_account_id"
                :items="subAccounts(item.type)"
                item-value="id"
                item-text="text"
                :placeholder="
                  item.sub_account_id
                    ? item.account_name + ' ' + item.account_number
                    : $t('Global.select')
                "
                :error-messages="errors"
                single-line
                :dense="errors.length == 0"
                :hide-details="errors.length == 0"
              >
              </v-select>
            </ValidationProvider>
          </template>
          <template v-slot:item.price="{ item }">
            <span v-if="percent && settings && settings.sub_account_split_type">
              <v-text-field
                single-line
                dense
                hide-details
                v-model="item.percent"
                type="number"
                min="0"
                :suffix="$t('Global.rate')"
                @blur="
                  item.percent = $numberFormatter.formatWithDotNoMoreThan(
                    item.percent,
                    2
                  )
                "
                style="max-width: 100px; margin-left: auto"
              />
            </span>
            <span v-else>
              <v-text-field
                single-line
                dense
                hide-details
                v-model="item.price"
                type="number"
                min="0"
                :prefix="$t('Global.money')"
                @blur="
                  item.price = $numberFormatter.formatWithDotNoMoreThan(
                    item.price,
                    2
                  )
                "
                style="max-width: 100px; margin-left: auto"
              />
            </span>
          </template>
        </v-data-table>
      </ValidationObserver>
      <v-card-actions>
        <span v-if="!sum_check" class="danger--text">
          {{ $t("MaterialTemplate.splitPrices.sum_check_error") }}
        </span>
        <span v-if="!sum_percent_check" class="danger--text">
          {{ $t("MaterialTemplate.splitPrices.sum_percent_check_error") }}
        </span>
        <v-spacer></v-spacer>
        <v-btn color="primary" outlined @click="dialog = false">
          {{ $t("$vuetify.close") }}
        </v-btn>
        <v-btn color="accent" @click="save()">
          {{ $t("CRUD.save") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>


<script>
import { mapState } from "vuex";
import i18n from "@/i18n";
import {
  ValidationObserver,
  ValidationProvider,
  setInteractionMode,
} from "vee-validate";
import SubAccounts from "@/api/sub_accounts";
import SubAccountSettings from "@/api/sub_account_settings";

setInteractionMode("eager");

export default {
  name: "SplitPrice",

  props: {
    value: Object,
    mode: String,
    bank_code: String,
    price: Number,
    percent: Boolean, //分账百分比
  },

  components: {
    ValidationProvider,
    ValidationObserver,
  },

  computed: {
    headers() {
      return [
        {
          text: i18n.t("MaterialTemplate.splitPrices.sub_account_type"),
          align: "center",
          sortable: true,
          value: "name",
        },
        {
          text: i18n.t("MaterialTemplate.splitPrices.account"),
          align: "start",
          sortable: true,
          value: "account",
        },
        {
          text: i18n.t("MaterialTemplate.splitPrices.price"),
          align: "end",
          sortable: true,
          value: "price",
        },
      ];
    },
    // 使用对象展开运算符将此对象混入到外部对象中
    ...mapState(["loading", "settings"]),
  },

  data: () => ({
    dialog: false,
    sub_account_settings: [],
    sub_account_prices: [],
    sub_accounts: [],
    sum_check: true,
    sum_price: true,
    sum_percent_check: true,
  }),

  mounted() {
    SubAccountSettings.fetchAll().then(
      function (response) {
        if (response.data.status.isOK()) {
          var sub_account_settings = response.data.sub_account_settings;
          if (sub_account_settings) {
            this.sub_account_settings = Object.keys(sub_account_settings).map(
              (sub_account_setting_key) => {
                return Object.assign(
                  { type: sub_account_setting_key },
                  sub_account_settings[sub_account_setting_key]
                );
              }
            );
          }
        }
      }.bind(this)
    );
  },

  watch: {
    value: {
      immediate: true, //初始赋值也调用
      handler() {
        // 重设数据时要重置校验报错
        this.sum_check = true;
        this.sum_percent_check = true;
        if (this.$refs && this.$refs.observer) this.$refs.observer.reset();
        if (this.sub_account_settings && this.value) {
          this.dialog = true;
          if (this.value.sub_account_price_json) {
            this.sub_account_prices = this.sub_account_settings.map(
              (sub_account_setting) => {
                sub_account_setting = Object.assign(
                  {},
                  sub_account_setting,
                  this.value.sub_account_price_json[sub_account_setting.type]
                );
                if (
                  this.percent &&
                  this.settings &&
                  this.settings.sub_account_split_type
                ) {
                  sub_account_setting.percent = sub_account_setting.percent
                    ? Number(
                        this.$numberFormatter.formatWithDot(
                          sub_account_setting.percent * 100,
                          2
                        )
                      )
                    : null;
                } else {
                  sub_account_setting.price = sub_account_setting.price
                    ? Number(sub_account_setting.price)
                    : null;
                }

                sub_account_setting.sub_account_id =
                  sub_account_setting.sub_account_id
                    ? Number(sub_account_setting.sub_account_id)
                    : null;
                return sub_account_setting;
              }
            );
          } else {
            this.sub_account_prices = JSON.parse(
              JSON.stringify(this.sub_account_settings)
            );
          }
        }
      },
    },
    bank_code: {
      immediate: true, //初始赋值也调用
      handler() {
        SubAccounts.fetch({
          bank_code: this.bank_code,
          delete_flgs: [0],
        }).then(
          function (response) {
            if (response.data.status.isOK()) {
              this.sub_accounts = response.data.sub_accounts;
            }
          }.bind(this)
        );
      },
    },
    dialog: {
      handler() {
        if (!this.dialog) {
          this.$emit("input", null);
        }
      },
    },
  },

  methods: {
    subAccounts(sub_account_type) {
      var sub_accounts = [
        {
          text: this.$t("Global.select"),
          id: null,
        },
      ];
      this.sub_accounts
        .filter(
          (sub_account) => sub_account.sub_account_type == sub_account_type
        )
        .forEach((sub_account) => {
          sub_account.text =
            sub_account.account_name + " " + sub_account.account_number;
          sub_accounts.push(sub_account);
        });
      return sub_accounts;
    },
    save() {
      // 做前端校验
      this.$refs.observer.validate().then(
        function (result) {
          if (result) {
            this.sum_price = 0;
            this.sum_percent = 0;
            this.value.sub_account_price_json = {};
            this.sub_account_prices.forEach((sub_price) => {
              if (sub_price.sub_account_id) {
                var sub_account = this.sub_accounts.find(
                  (sub_account) => sub_account.id == sub_price.sub_account_id
                );
                if (
                  this.percent &&
                  this.settings &&
                  this.settings.sub_account_split_type
                ) {
                  this.sum_percent += Number(
                    this.$numberFormatter.formatWithDot(
                      sub_price.percent / 100,
                      4
                    )
                  );
                } else {
                  this.sum_price += sub_price.price;
                }

                this.value.sub_account_price_json[sub_price.type] = {
                  account_name: sub_account.account_name,
                  account_number: sub_account.account_number,
                  sub_account_id: sub_account.id,
                  sub_account_type: sub_account.sub_account_type,
                  sub_account_type_name: sub_account.sub_account_type_name,
                };
                if (
                  this.percent &&
                  this.settings &&
                  this.settings.sub_account_split_type
                ) {
                  this.value.sub_account_price_json[sub_price.type] =
                    Object.assign(
                      {},
                      this.value.sub_account_price_json[sub_price.type],
                      {
                        percent: this.$numberFormatter
                          .formatWithDot(sub_price.percent / 100, 4)
                          .replace(/\.?0*$/, ""),
                      }
                    );
                } else {
                  this.value.sub_account_price_json[sub_price.type] =
                    Object.assign(
                      {},
                      this.value.sub_account_price_json[sub_price.type],
                      {
                        price: sub_price.price,
                      }
                    );
                }
              }
            });
            if (
              this.percent &&
              this.settings &&
              this.settings.sub_account_split_type
            ) {
              this.sum_percent_check =
                this.$numberFormatter.formatWithDot(this.sum_percent, 4) == 1;
              if (this.sum_percent_check) {
                this.$emit("input", this.value);
                this.dialog = false;
              }
            } else {
              this.sum_check =
                this.$numberFormatter.formatWithMoney(this.sum_price) ==
                this.$numberFormatter.formatWithMoney(this.price);
              if (this.sum_check) {
                this.$emit("input", this.value);
                this.dialog = false;
              }
            }
          }
        }.bind(this)
      );
    },
  },
};
</script>